import { captureAdminApiError } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  checkVoucherCode,
  createVoucher,
  deleteVoucher,
  duplicateVoucher,
  getVoucher,
  getVouchers,
  updateVoucher,
  updateVoucherDisplay,
  updateVoucherStatus,
  uploadVoucherBanner,
} from '../api';
import { VOUCHER_QUERY_KEY } from '../constants';
import type {
  CreateVoucherPayload,
  VoucherFilters,
  VoucherUpdateBasicInfoPayload,
  VoucherUpdateStatusPayload,
} from '../types';

const captureVoucherMutationError = (options: {
  error: unknown;
  requestPath: string;
  httpMethod: 'POST' | 'PATCH' | 'DELETE';
  queryKey: string;
  operation: string;
  extras?: Record<string, unknown>;
}) => {
  captureAdminApiError({
    error: options.error,
    requestPath: options.requestPath,
    httpMethod: options.httpMethod,
    queryKey: options.queryKey,
    operation: options.operation,
    feature: 'voucher',
    extras: options.extras,
  });
};

export const useVouchers = (filters?: VoucherFilters) => {
  const session = useAuthStore();

  return useQuery({
    queryKey: [VOUCHER_QUERY_KEY, filters],
    queryFn: () => getVouchers(session.access_token, filters),
    enabled: !!session.access_token,
  });
};

export const useVoucher = (id: string) => {
  const session = useAuthStore();

  return useQuery({
    queryKey: [VOUCHER_QUERY_KEY, id],
    queryFn: () => getVoucher(session.access_token, id),
    enabled: !!session.access_token && !!id,
  });
};

export const useCreateVoucherMutation = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}) => {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVoucherPayload) =>
      createVoucher(session.access_token, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VOUCHER_QUERY_KEY] });
      options?.onSuccess?.();
    },
    onError: (error) => {
      captureVoucherMutationError({
        error,
        requestPath: '/backoffice/voucher/create',
        httpMethod: 'POST',
        queryKey: 'create-voucher',
        operation: 'create_voucher',
      });
      options?.onError?.(
        error instanceof Error ? error : new Error(String(error))
      );
    },
    onSettled: options?.onSettled,
  });
};

export const useUpdateVoucherMutation = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}) => {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: VoucherUpdateBasicInfoPayload;
    }) => updateVoucher(session.access_token, id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [VOUCHER_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [VOUCHER_QUERY_KEY, id] });
      options?.onSuccess?.();
    },
    onError: (error, { id }) => {
      captureVoucherMutationError({
        error,
        requestPath: `/backoffice/voucher/${id}`,
        httpMethod: 'PATCH',
        queryKey: 'update-voucher',
        operation: 'update_voucher',
        extras: { voucherId: id },
      });
      options?.onError?.(
        error instanceof Error ? error : new Error(String(error))
      );
    },
    onSettled: options?.onSettled,
  });
};

export const useDeleteVoucherMutation = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}) => {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteVoucher(session.access_token, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VOUCHER_QUERY_KEY] });
      options?.onSuccess?.();
    },
    onError: (error, id) => {
      captureVoucherMutationError({
        error,
        requestPath: `/backoffice/voucher/${id}/delete`,
        httpMethod: 'DELETE',
        queryKey: 'delete-voucher',
        operation: 'delete_voucher',
        extras: { voucherId: id },
      });
      options?.onError?.(
        error instanceof Error ? error : new Error(String(error))
      );
    },
    onSettled: options?.onSettled,
  });
};

export const useDuplicateVoucherMutation = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}) => {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => duplicateVoucher(session.access_token, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VOUCHER_QUERY_KEY] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

export const useUpdateVoucherStatusMutation = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}) => {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: VoucherUpdateStatusPayload['status'];
    }) => updateVoucherStatus(session.access_token, id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VOUCHER_QUERY_KEY] });
      options?.onSuccess?.();
    },
    onError: (error, { id }) => {
      captureVoucherMutationError({
        error,
        requestPath: `/backoffice/voucher/${id}/update-status`,
        httpMethod: 'PATCH',
        queryKey: 'update-voucher-status',
        operation: 'update_voucher_status',
        extras: { voucherId: id },
      });
      options?.onError?.(
        error instanceof Error ? error : new Error(String(error))
      );
    },
    onSettled: options?.onSettled,
  });
};

export const useUpdateVoucherDisplayMutation = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}) => {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isDisplay }: { id: string; isDisplay: boolean }) =>
      updateVoucherDisplay(session.access_token, id, isDisplay),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VOUCHER_QUERY_KEY] });
      options?.onSuccess?.();
    },
    onError: (error, { id }) => {
      captureVoucherMutationError({
        error,
        requestPath: `/backoffice/voucher/${id}/update-display`,
        httpMethod: 'PATCH',
        queryKey: 'update-voucher-display',
        operation: 'update_voucher_display',
        extras: { voucherId: id },
      });
      options?.onError?.(
        error instanceof Error ? error : new Error(String(error))
      );
    },
    onSettled: options?.onSettled,
  });
};

export const useCheckVoucherCodeMutation = (options?: {
  onSuccess?: (isValid: boolean) => void;
  onError?: (error: Error) => void;
}) => {
  const session = useAuthStore();

  return useMutation({
    mutationFn: (code: string) =>
      checkVoucherCode(session.access_token, code.trim()),
    onSuccess: (data) => {
      options?.onSuccess?.(data.is_valid);
    },
    onError: options?.onError,
  });
};

export const useUploadVoucherBannerMutation = (options?: {
  onSuccess?: (result: { url: string; filename: string }) => void;
  onError?: (error: Error) => void;
}) => {
  const session = useAuthStore();

  return useMutation({
    mutationFn: (file: File) => uploadVoucherBanner(session.access_token, file),
    onSuccess: (result) => {
      options?.onSuccess?.(result);
    },
    onError: options?.onError,
  });
};

export * from './use-voucher-code';
export * from './use-voucher-preview';
export * from './use-voucher-product-form';
export * from './use-voucher-target-form';
